import { MRO_CART_TESTIDS } from "@swiggy-private/cart-test-ids";
import { useIsomorphicLayoutEffect } from "@swiggy-private/core-ui";
import { format } from "@swiggy-private/js-utils";
import { RefObject } from "react";

const SECTION_ANIMATION_DURATION_MS = 300;
// MRO per-RX menu section root carries data-testid="mro-rx-cart-container-<restaurantId>"
// (see MRO_CART_TESTIDS.CONTAINER). We animate exactly these when added/removed.
const MRO_SECTION_SELECTOR = `[data-testid^="${format(MRO_CART_TESTIDS.CONTAINER, "")}"]`;
// Marks the transient snapshot node we reinsert to animate a removal, so our own
// insert/remove of it never re-triggers the observer's enter/exit handlers.
const COLLAPSE_CLONE_ATTR = "data-mro-collapse-clone";

const prefersReducedMotion = (): boolean =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const isCollapseClone = (node: Node): boolean =>
    node instanceof HTMLElement &&
    (node.hasAttribute(COLLAPSE_CLONE_ATTR) || !!node.closest(`[${COLLAPSE_CLONE_ATTR}]`));

/**
 * Grow-in: animate a newly added section's height 0 -> natural (with fade) so it
 * expands in place and pushes the content below smoothly. `overflow: hidden` is
 * applied only for the animation's duration.
 */
const growSectionIn = (element: HTMLElement): void => {
    if (typeof element.animate !== "function" || prefersReducedMotion()) {
        return;
    }
    const targetHeight = element.getBoundingClientRect().height;
    if (targetHeight <= 0) {
        return;
    }

    const previousOverflow = element.style.overflow;
    element.style.overflow = "hidden";
    const animation = element.animate(
        [
            { height: "0px", opacity: 0 },
            { height: `${targetHeight}px`, opacity: 1 },
        ],
        { duration: SECTION_ANIMATION_DURATION_MS, easing: "ease-in-out" },
    );
    const restore = (): void => {
        element.style.overflow = previousOverflow;
    };
    animation.onfinish = restore;
    animation.oncancel = restore;
};

/**
 * Collapse-out: React unmounts the removed node instantly, so we can't animate it
 * directly. Instead we reinsert a static clone at the node's original position
 * (this runs in the MutationObserver microtask, before the browser paints, so
 * there's no visible jump), animate the clone's height natural -> 0, then discard it.
 */
const collapseSectionOut = (removedNode: Node, parent: Node, nextSibling: Node | null): void => {
    if (!(removedNode instanceof HTMLElement) || !(parent instanceof HTMLElement)) {
        return;
    }
    // Don't collapse-animate our own clones (removing a clone reports a removal too).
    if (isCollapseClone(removedNode)) {
        return;
    }
    if (typeof removedNode.animate !== "function" || prefersReducedMotion() || !parent.isConnected) {
        return;
    }

    const clone = removedNode.cloneNode(true) as HTMLElement;
    clone.setAttribute(COLLAPSE_CLONE_ATTR, "true");
    // Static snapshot: neutralise ids and interactions for the transient lifetime.
    clone.removeAttribute("id");
    clone.style.pointerEvents = "none";

    const referenceNode = nextSibling && (nextSibling as ChildNode).isConnected ? nextSibling : null;
    try {
        parent.insertBefore(clone, referenceNode);
    } catch {
        return;
    }

    const startHeight = clone.getBoundingClientRect().height;
    if (startHeight <= 0) {
        clone.remove();
        return;
    }

    clone.style.overflow = "hidden";
    const animation = clone.animate(
        [
            { height: `${startHeight}px`, opacity: 1 },
            { height: "0px", opacity: 0 },
        ],
        { duration: SECTION_ANIMATION_DURATION_MS, easing: "ease-in-out" },
    );
    const cleanup = (): void => clone.remove();
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
};

// Collect MRO section elements from an added node (the node itself or its subtree).
const collectSections = (node: Node): HTMLElement[] => {
    if (!(node instanceof HTMLElement)) {
        return [];
    }
    const sections: HTMLElement[] = [];
    if (node.matches?.(MRO_SECTION_SELECTOR)) {
        sections.push(node);
    }
    node.querySelectorAll?.(MRO_SECTION_SELECTOR)?.forEach((el) => sections.push(el as HTMLElement));
    return sections;
};

// Whether a removed node is (or contains) an MRO section worth collapse-animating.
const containsSection = (node: Node): boolean => {
    if (!(node instanceof HTMLElement)) {
        return false;
    }
    return node.matches?.(MRO_SECTION_SELECTOR) || !!node.querySelector?.(MRO_SECTION_SELECTOR);
};

/**
 * MRO-only. Animates RX menu sections as they enter/leave the cart on tab toggle:
 * added sections grow in, removed sections collapse out — both pushing the content
 * below smoothly instead of popping.
 *
 * - Sections present on the initial render are NOT animated (observer reacts to
 *   post-mount mutations only).
 * - Filtered to the MRO section testid, so unrelated DOM churn (tooltips, croutons,
 *   image loads) never triggers an animation.
 * - Removal uses a transient snapshot clone (marked so it can't recurse).
 * - Fully encapsulated: the consuming component only attaches `containerRef`.
 */
export const useMROCartItemSectionAnimation = (containerRef: RefObject<HTMLElement>, enabled: boolean): void => {
    useIsomorphicLayoutEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container || typeof MutationObserver === "undefined") {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (isCollapseClone(node)) {
                        return;
                    }
                    collectSections(node).forEach(growSectionIn);
                });

                mutation.removedNodes.forEach((node) => {
                    if (isCollapseClone(node) || !containsSection(node)) {
                        return;
                    }
                    collapseSectionOut(node, mutation.target, mutation.nextSibling);
                });
            });
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            // Remove any in-flight collapse clones on teardown.
            container.querySelectorAll(`[${COLLAPSE_CLONE_ATTR}]`).forEach((clone) => clone.remove());
        };
    }, [containerRef, enabled]);
};
